import * as React from 'react';
import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import PDFHeader from '../../../components/PDF/PDFLayout/PDFHeader';
import PDFFooter from '../../../components/PDF/PDFLayout/PDFFooter';
import { IProjectMacra2 } from '../../../MACRA_2/store/project/projectSlice';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { PdfCrossCheck, styles } from '../../../components/PDF/PDFStyle';
import { ISlopedGabionResults } from '../../../MACRA_2/api/IApiResponse';
import { useTranslation } from 'react-i18next';
import { roundToThree, roundToTwo } from '../../../MACRA_1/components/InputForm/Geometry/utils';
import PDFTable from '../PDFTable/PDFTable';
import { IWeirGeometrySlopedWeirForm } from '../../../MACRA_2/components/InputForm/WeirGeometry/IWeirGeometry';
import { conversionLabel } from '../../../conversion';
import { IHydraulicDataSlopedWeirForm } from '../../../MACRA_2/components/InputForm/HydraulicData/IHydraulicData';
interface IPDFSlopedProps {
    project: IProjectMacra2;
    projectInfo: IProjectInfoForm;
    results: ISlopedGabionResults;
}

const PDFSloped = (props: IPDFSlopedProps) => {
    const { t } = useTranslation();
    const { project, projectInfo, results } = props;

    const tableHydraulicInput = [
        [t('output_hydraulic_stepped_sloped_discharge'), 'Q', `${results.hydraulic.designDischarge}`, `${conversionLabel.m3s_ft3s[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_riverbed_gradient'), 'i', `${roundToTwo(project.hydraulicData?.riverBedGradient)}`, `-`],
        [
            t('output_hydraulic_stepped_sloped_roughnessCoefficient'),
            'n',
            `${roundToThree(project.hydraulicData?.roughnessCoefficient)}`,
            '-',
        ],
        [t('output_hydraulic_sloped_chute_width'), 'W', `${roundToTwo(results.hydraulic.wingsWidth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_chute_slope'), '@S(0)', `${roundToTwo(results.hydraulic.chuteSlope)}`, '-'],
        [
            t('output_hydraulic_sloped_chute_height'),
            '@H(c)',
            `${roundToTwo((project.weirGeometry as IWeirGeometrySlopedWeirForm).height)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
        ],
        [t('output_hydraulic_sloped_crest_length'), '@L(c)', `${roundToTwo(results.hydraulic.crestLenght)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
    ];

    const tableHydraulicOutput = [
        [t('output_hydraulic_sloped_unit_discharge'), 'q', `${roundToTwo(results.hydraulic.specificDischargeAtCrest)}`, `${conversionLabel.m3s_ft3s[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_backwater_depth_upstream'), '@Z(0)', `${roundToTwo(results.hydraulic.upstreamSection)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_critical_height_crest'), '@h(g)', `${roundToTwo(results.hydraulic?.criticalHeight)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_critical_velocity_crest'), '@v(g)', `${roundToTwo(results.hydraulic.criticalVelocity)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_critical_head_crest'), '@H(g)', `${roundToTwo(results.hydraulic.criticalHeadAtCrest)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
    ];

    const tableHydraulicOutputSlopedParameter = [
        [t('output_hydraulic_sloped_velocity_downstream'), '@V(1)', `${roundToTwo(results.basinDesign.downstreamVelocity)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_water_depth_downstream'), '@z(1)', `${roundToTwo(results.basinDesign.downstreamWaterDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_froude_number'), 'Fr', `${roundToTwo(results.basinDesign.froudeNumber)}`, `-`],
        [t('output_hydraulic_sloped_conjugated_height'), '@z(2)', `${roundToTwo(results.basinDesign.conjugateDepth)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_hydraulic_jump_length'), '@L(j)', `${roundToTwo(results.basinDesign.jumpLength)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_water_depth_downstream_structure'), '@z(3)', `${roundToTwo(results.hydraulic.z3)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`],
        [t('output_hydraulic_sloped_velocity_downstream_structure'), '@v(3)', `${roundToTwo(results.hydraulic.v3)}`, `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`],
    ];

    const tableChuseAndBasinDOutput = [
        [`${t('output_hydraulic_sloped_stilling_basin_width')}`, '@W(b)', `${roundToTwo(project.stillingBasin?.width)}`, `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`, '-'],
        [
            `${t('output_hydraulic_sloped_chute_lining_thickness')}`,
            '@t(c)',
            `${roundToTwo((project.weirGeometry as IWeirGeometrySlopedWeirForm).liningThickness)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
            '-',
        ],
        [
            `${t('output_hydraulic_sloped_minimum_length_stilling_basin')}`,
            '@L(b_min)',
            `${roundToTwo(results.basinDesign.jumpLength)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
            project.stillingBasin?.poolLength! >= roundToTwo(results.basinDesign.jumpLength) ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [
            `${t('output_hydraulic_sloped_minimum_counterweir_height')}`,
            '@f(c_min)',
            `${roundToTwo(results.basinDesign.minCounterweirHeight)}`,
            `${conversionLabel.m_ft[projectInfo.unitOfMeasure as number]}`,
            project.stillingBasin?.counterweirElevation! >= roundToTwo(results.basinDesign.minCounterweirHeight) ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [
            `${t('output_hydraulic_vertical_stepped_sloped_lining_material')}`,
            `${project.stillingBasin?.material.name!}`,
            `t=${roundToTwo(project.stillingBasin?.material.physicalData![0].thickness)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `d50=${roundToTwo(project.stillingBasin?.material.physicalData![0].rockD50)} ${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            `Cu=${roundToTwo(project.stillingBasin?.material.physicalData![0].cu)}`,
        ],
        [
            `${t('output_hydraulic_sloped_basin_lining_thickness')}`,
            '@t(b)',
            `${roundToTwo(project.stillingBasin?.material.physicalData![0].thickness)}`,
            `${conversionLabel.m_in[projectInfo.unitOfMeasure as number]}`,
            '-',
        ],
        [
            `${t('output_hydraulic_sloped_velocity_under_lining')}`,
            '@v(1)',
            `${roundToTwo(results.basinDesign.residualVelocityUnderGeotextile)}`,
            `${conversionLabel.ms_fts[projectInfo.unitOfMeasure as number]}`,
            (project.hydraulicData as IHydraulicDataSlopedWeirForm).soilAdmissibleVelocity >= roundToTwo(results.basinDesign.residualVelocityUnderGeotextile)
                ? PdfCrossCheck.check
                : PdfCrossCheck.cross,
        ],
        [
            `${t('output_hydraulic_vertical_stepped_sloped_shear_stress_downstream_section')}`,
            '@τ(c)',
            `${roundToTwo(results.basinDesign.shearStress)}`,
            `${conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number]}`,
            roundToTwo(results.basinDesign.shearStress) < project.stillingBasin?.material.physicalData![0].allowShearStress! ? PdfCrossCheck.check : PdfCrossCheck.cross,
        ],
        [
            `${t('output_hydraulic_vertical_stepped_sloped_allowable_shear_stress_lining')}`,
            '@τ(all)',
            `${roundToTwo(project.stillingBasin?.material.physicalData![0].allowShearStress)}`,
            `${conversionLabel.Nm2_lbfft2[projectInfo.unitOfMeasure as number]}`,
            '-',
        ],
    ];

    return (
        <View>
            <View style={styles.rowSpaced}>
                <Text style={[styles.boldGreen, styles.py10]}>{t('graphical_results_hydraulic')}</Text>
            </View>
            <PDFTable columns={[t('output_table_title_input'), t('output_symbol'), t('output_value'), t('output_unit')]} data={tableHydraulicInput} />
            <PDFTable columns={[t('output_hydraulic_sloped_upstream_parameters'), t('output_symbol'), t('output_value'), t('output_unit')]} data={tableHydraulicOutput} />
            <PDFTable columns={[t('output_hydraulic_sloped_downstream_parameters'), t('output_symbol'), t('output_value'), t('output_unit')]} data={tableHydraulicOutputSlopedParameter} />

            <View style={styles.rowSpaced}>
                <Text style={[styles.boldGreen, styles.py10]}>{t('graphical_results_basinDesign')}</Text>
            </View>
            <PDFTable
                columns={[t('output_hydraulic_sloped_chute_and_basin_design_output'), t('output_symbol'), t('output_value'), t('output_unit'), t('output_check')]}
                data={tableChuseAndBasinDOutput}
            />
        </View>
    );
};

export default PDFSloped;
